<template>
  <div class="p-exhibition">
    <TitleHeader
      data-testid="exhibitionTitle"
      :heading="pageTitle"
      :subhead="exhibitionSeries || exhibitionLabel"
      :has-details="exhibitionDate != undefined"
    >
      <template #details>
        <p v-if="exhibitionDate" ref="exhibitionDate">
          {{ exhibitionDate }}
        </p>
      </template>
    </TitleHeader>

    <Module :title="exhibitionDetailsLabel" />
    <div class="p-exhibition__description_and_location">
      <div
        v-if="description != undefined || departmentInfo != undefined"
        class="p-exhibition__description_wrapper"
      >
        <div v-if="description != undefined" class="p-exhibition__description">
          <RichText :content="description" />
        </div>
        <div
          v-if="departmentInfo != undefined"
          class="p-exhibition__department"
        >
          <RichText :content="departmentInfo" />
        </div>
      </div>
      <div :class="locationClasses">
        <div
          v-if="exhibitionGettyLocation"
          class="p-exhibition__getty_location"
        >
          <div class="p-exhibition__getty_location_campus">
            {{ exhibitionGettyLocation.campus }}
          </div>
          <div class="p-exhibition__getty_location_pavilion_floor">
            {{ exhibitionGettyLocation.pavilionOrFloors }}
          </div>
          <div class="p-exhibition__current_exhibition">
            {{ currentExhibitionLabel }}
          </div>
          <GuiLink
            :href="exhibitionLink"
            class="p-exhibition__current_exhibition_link"
          >
            {{ exhibitionLinkLabel }}
          </GuiLink>
        </div>
        <div v-if="pastExhibition" class="p-exhibition__past_exhibition">
          {{ pastExhibitionLabel }}
        </div>
        <div v-if="futureExhibition" class="p-exhibition__past_exhibition">
          {{ futureExhibitionLabel }}
        </div>
      </div>
    </div>
    <div v-if="exhibitionResources?.length > 0" class="p-exhibition__resources">
      <Module :title="exhibitionResourcesLabel" :modifiers="['resources']" />
      <div class="p-exhibition__resources_cardlist">
        <CardList
          card-name="MenuCard"
          :title="exhibitionResourcesLabel"
          :items="exhibitionResources"
        />
      </div>
    </div>
    <ExhibitionVenues v-if="exhibition?.venues" :exhibition="exhibition" />
    <TechnicalData :metadata-fields="technicalMetadataFields" />
  </div>
</template>

<script>
import {
  CardList,
  GuiLink,
  Module,
  RichText,
  TechnicalData,
  useLocalization,
  useEventBus
} from "@thegetty-private/getty-ui";
import ExhibitionVenues from "@/components/organisms/exhibitionVenues/index.vue";
import TitleHeader from "@/components/atoms/titleHeader/index.vue";
import { useHead } from "@unhead/vue";
import { ref } from "vue";
import { useExhibitionStore } from "@/stores/exhibition.js";
import { mapStores } from "pinia";

const metaTitle = ref(null);
const metaDescription = ref(null);
export default {
  name: "ExhibitionPage",
  components: {
    ExhibitionVenues,
    GuiLink,
    CardList,
    Module,
    RichText,
    TechnicalData,
    TitleHeader
  },
  inject: ["updatePageTitle"],
  props: {
    /**
     * The indexedId of the Exhibition LOD object
     */
    indexedId: {
      type: String,
      default: undefined
    },
    /**
     * a way to inject a static object into the page
     */
    storybookData: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus(),
      exhibitionLabel: this.translatePart("exhibition_page.exhibition"),
      exhibitionDetailsLabel: this.translatePart(
        "exhibition_page.exhibition_details"
      ),
      // variable that watches/flips after getty location action has completed, so data on page does not display prematurely
      pastExhibitionLabel: this.translatePart(
        "exhibition_page.past_exhibition"
      ),
      currentExhibitionLabel: this.translatePart(
        "exhibition_page.current_exhibition"
      ),
      futureExhibitionLabel: this.translatePart(
        "exhibition_page.future_exhibition"
      ),
      exhibitionLinkLabel: this.translatePart(
        "exhibition_page.exhibition_link"
      ),
      exhibitionResourcesLabel: this.translatePart(
        "exhibition_page.related_resources.label"
      )
    };
  },
  computed: {
    ...mapStores(useExhibitionStore),
    /**
     * Returns the exhibition's PropositionalObject JSON-LD from the store
     * @returns {object}
     */
    exhibition() {
      if (this.storybookData) {
        return this.storybookData;
      }
      return this.exhibitionStore.getExhibition(this.indexedId);
    },
    /**
     * Returns the Getty location to display if the exhibition is currently live
     * FIXME: only return Getty location for now, but has more detailed info available
     *
     * @returns {string}
     */
    exhibitionGettyLocation() {
      return this?.exhibition?.gettyLocation;
    },
    /**
     * The exhibition's department(s)
     */
    departmentInfo() {
      return this?.exhibition?.departmentInfo;
    },
    /**
     * The description
     * @returns {string}
     */
    description() {
      return this?.exhibition?.description;
    },
    /**
     * If there are multiple venues, gets the combined timespan for all venues/activities related to the Exhibition.
     * If there is only one venue, use that venue's timespan string.
     *
     * @returns {string}
     */
    exhibitionDate() {
      return this?.exhibition?.dates;
    },
    /**
     * The link to the active exhibition page on Getty.edu
     */
    exhibitionLink() {
      return this?.exhibition?.link;
    },
    /**
     * The series that the exhibition is part of
     */
    exhibitionSeries() {
      return this?.exhibition?.series;
    },
    /**
     * The Exhibition title
     */
    pageTitle() {
      return this?.exhibition?.pageTitle;
    },
    /**
     * Returns which classes to use depending on what data is available
     * (adds a separation border)
     *
     * @returns {array}
     */
    locationClasses() {
      let classes = ["p-exhibition__location_and_status"];

      if (this.description != undefined || this.departmentInfo != undefined) {
        classes.push("p-exhibition__getty_location_border");
      }

      return classes;
    },
    /**
     * The metadata fields for the technical data section for the Exhibition
     */
    technicalMetadataFields() {
      return this?.exhibition?.technicalMetadata;
    },
    /**
     * exhibition resources
     */
    exhibitionResources() {
      return this?.exhibition?.resources;
    },
    /**
     * if this is a past exhibition as determined by the /exhibitionLocations response
     */
    pastExhibition() {
      return this?.exhibition?.pastExhibition;
    },
    /**
     * if this is a future getty exhibition as determined by the venues list/dates
     */
    futureExhibition() {
      return this?.exhibition?.futureExhibition;
    }
  },
  async created() {
    await this.fetchData();
  },
  mounted() {
    useHead({
      title: metaTitle,
      titleTemplate: null,
      meta: [
        // these fields will render as <meta name="" content="">
        {
          name: "title",
          content: this.translatePart("exhibition_page.metadata_title")
        },

        {
          name: "description",
          content: metaDescription
        }
      ]
    });
  },
  methods: {
    async fetchData() {
      if (this.indexedId == undefined) {
        return;
      }
      await this.exhibitionStore.addExhibition(
        this.indexedId,
        this.translatePart
      );
      this.updatePageTitle(this.pageTitle);

      metaTitle.value = this.pageTitle;
      metaDescription.value = this.description;

      // check for metadata to send for analytics
      this.eventBus.emit("PageMetadata", {
        tmsId: this.exhibition?.technicalMetadata?.other_ids?.tms_id?.name
      });

      this.eventBus.emit("data-ready", {
        heading: this.pageTitle,
        subheading: this.exhibitionDate
      });
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
